import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n, StatusBadge, GsdReadableBlock } from "@/coquille-bridge";
import { DataTable } from "@/components/DataTable.jsx";
import { EntityCrudPanel, demoFlag } from "@/components/admin/EntityCrudPanel.jsx";
import { DemoBadge } from "@/components/DemoBadge.jsx";
import { useDataStore } from "@/components/admin/useDataStore.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { getDemoContextForAccount } from "@/data/dojoOsOrgModel.js";
import { addTitleApplication, updateTitleApplication } from "@/data/dataStore.js";
import { EvidenceSummaryBlock } from "@/components/authority/KarateAuthorityDocumentsPanels.jsx";
import { notifyDataUpdate } from "@/components/admin/useDataStore.js";
import {
  INTERNAL_REFERRAL_FR,
  INTERNAL_REFERRAL_EN,
} from "@/data/demoTitleRecognitionData.js";
import { StudentProofsExplanation } from "@/components/authority/KarateAuthorityDocumentsPanels.jsx";
import {
  TITLE_TYPES,
  TITLE_STATUSES,
  APPLICATION_STATUSES,
  REGISTER_STATUSES,
  AUTHORITY_STATUSES,
  CERTIFICAT_STATUSES,
  titleLabel,
} from "@/utils/titleRecognitionLabels.js";
import { localizeDemoText } from "@/i18n/localizeDemoText.js";

function useLocale() {
  const { lang } = useI18n();
  return lang === "en" ? "en" : "fr";
}

export function InternalReferentialNotice({ testId = "title-internal-referential-notice" }) {
  const locale = useLocale();
  return (
    <GsdReadableBlock testId={testId}>
      <p className="title-module-notice">
        <strong>{locale === "en" ? "Important" : "Important"}</strong> —{" "}
        {locale === "en" ? INTERNAL_REFERRAL_EN : INTERNAL_REFERRAL_FR}
      </p>
    </GsdReadableBlock>
  );
}

export function TerminologyPanel({ testId = "title-terminology-panel" }) {
  const locale = useLocale();
  const rows =
    locale === "en"
      ? [
          ["Grade (belt)", "1st dan, 5th dan, 10th dan", "Belts / grades module"],
          ["Martial title", "Sensei, Shihan, Renshi, Kyoshi, Hanshi", "Titles & recognitions"],
          ["App role", "Admin, Sensei, Sempai, Parent, Student", "Auth / navigation"],
          ["Authority", "Recommend, review, approve, grant", "Titles & recognitions"],
        ]
      : [
          ["Grade (ceinture)", "1er dan, 5e dan, 10e dan", "Ceintures / grades"],
          ["Titre martial", "Sensei, Shihan, Renshi, Kyoshi, Hanshi", "Titres & reconnaissances"],
          ["Rôle applicatif", "Admin, Sensei, Sempai, Parent, Étudiant", "Auth / navigation"],
          ["Autorité", "Recommander, réviser, approuver, attitrer", "Titres & reconnaissances"],
        ];

  return (
    <GsdReadableBlock testId={testId}>
      <h3>{locale === "en" ? "Grade vs title vs role" : "Grade vs titre vs rôle"}</h3>
      <table className="gsd-admin-panel__table data-table" data-testid="title-terminology-table">
        <thead>
          <tr>
            <th>{locale === "en" ? "Element" : "Élément"}</th>
            <th>{locale === "en" ? "Example" : "Exemple"}</th>
            <th>{locale === "en" ? "Module" : "Module"}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([el, ex, mod]) => (
            <tr key={el}>
              <td><strong>{el}</strong></td>
              <td>{ex}</td>
              <td className="muted">{mod}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted">
        {locale === "en"
          ? "A martial title is earned through experience, grade, teaching years, contribution, documents and competent authority validation — not assigned as an app role."
          : "Un titre martial se gagne par l'expérience, le grade, les années d'enseignement, la contribution, les documents et la validation par l'autorité compétente — il ne se confond pas avec un rôle applicatif."}
      </p>
    </GsdReadableBlock>
  );
}

export function MartialTitlesReferentialPanel({ allowCrud = false, testId = "martial-titles-panel" }) {
  const locale = useLocale();
  const store = useDataStore();
  const rows = [...store.martialTitles].sort((a, b) => a.niveau - b.niveau);

  const columns = [
    {
      key: "nom",
      label: locale === "en" ? "Title" : "Titre",
      render: (r) => (
        <>
          {localizeDemoText(r.nom, locale)} {r.nom_japonais ? <span className="muted">({r.nom_japonais})</span> : null}{" "}
          {demoFlag(r)}
        </>
      ),
    },
    {
      key: "type",
      label: locale === "en" ? "Type" : "Type",
      render: (r) => titleLabel(TITLE_TYPES, r.type, locale),
    },
    {
      key: "grade_minimum",
      label: locale === "en" ? "Min. grade" : "Grade min.",
      render: (r) => localizeDemoText(r.grade_minimum, locale),
    },
    {
      key: "annees_pratique_minimum",
      label: locale === "en" ? "Practice yrs" : "Ans pratique",
    },
    {
      key: "annees_enseignement_minimum",
      label: locale === "en" ? "Teaching yrs" : "Ans enseign.",
    },
    {
      key: "statut",
      label: locale === "en" ? "Status" : "Statut",
      render: (r) => (
        <StatusBadge
          label={titleLabel(TITLE_STATUSES, r.statut, locale)}
          tone={r.statut === "a_valider" ? "yellow" : r.statut === "futur" ? "neutral" : "blue"}
          testId={`title-ref-status-${r.id}`}
        />
      ),
    },
    {
      key: "documents_requis",
      label: locale === "en" ? "Required docs" : "Documents requis",
      render: (r) => (
        <span className="muted">{localizeDemoText(r.documents_requis?.slice(0, 50), locale)}…</span>
      ),
    },
  ];

  if (allowCrud) {
    return (
      <div data-testid={testId}>
        <InternalReferentialNotice />
        <EntityCrudPanel
          collectionKey="martialTitles"
          rows={store.martialTitles}
          testId="admin-titles-crud"
          searchFields={["nom", "type", "grade_minimum"]}
          columns={columns}
          fields={[
            { name: "id", label: "ID", required: true },
            { name: "nom", label: locale === "en" ? "Name" : "Nom", required: true },
            { name: "nom_japonais", label: locale === "en" ? "Japanese name" : "Nom japonais" },
            {
              name: "type",
              label: "Type",
              options: Object.entries(TITLE_TYPES).map(([v, l]) => ({
                value: v,
                label: locale === "en" ? l.en : l.fr,
              })),
            },
            { name: "niveau", label: locale === "en" ? "Level" : "Niveau", required: true },
            { name: "grade_minimum", label: locale === "en" ? "Min. grade" : "Grade minimum" },
            { name: "annees_pratique_minimum", label: locale === "en" ? "Practice years" : "Années pratique" },
            { name: "annees_enseignement_minimum", label: locale === "en" ? "Teaching years" : "Années enseignement" },
            { name: "documents_requis", label: locale === "en" ? "Required documents" : "Documents requis" },
            { name: "description", label: "Description" },
            {
              name: "statut",
              label: locale === "en" ? "Status" : "Statut",
              options: Object.entries(TITLE_STATUSES).map(([v, l]) => ({
                value: v,
                label: locale === "en" ? l.en : l.fr,
              })),
            },
          ]}
          buildItem={(form, existingId) => ({
            id: existingId || form.id.trim(),
            nom: form.nom.trim(),
            nom_japonais: form.nom_japonais?.trim() || "",
            type: form.type || "enseignement",
            niveau: Number(form.niveau) || 1,
            grade_minimum: form.grade_minimum.trim(),
            annees_pratique_minimum: Number(form.annees_pratique_minimum) || 0,
            annees_enseignement_minimum: Number(form.annees_enseignement_minimum) || 0,
            documents_requis: form.documents_requis?.trim() || "",
            description: form.description?.trim() || "",
            statut: form.statut || "a_valider",
            notes_validation: locale === "en" ? "Demo entry — to validate." : "Entrée démo — à valider.",
            demo: true,
          })}
        />
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <p className="muted">
        {locale === "en"
          ? "Possible titles — criteria to confirm with the club lead."
          : "Titres possibles — critères à confirmer avec le responsable."}
      </p>
      <DataTable
        rows={rows}
        columns={columns}
        searchFields={["nom", "type"]}
        filters={[
          {
            field: "statut",
            label: locale === "en" ? "Status" : "Statut",
            options: Object.entries(TITLE_STATUSES).map(([v, l]) => ({
              value: v,
              label: locale === "en" ? l.en : l.fr,
            })),
          },
        ]}
        emptyMessage={locale === "en" ? "No titles found." : "Aucun titre trouvé."}
        testId={`${testId}-table`}
      />
    </div>
  );
}

function ApplicationDetail({ app, locale, onAdminAction, canAdmin = false, testId }) {
  const store = useDataStore();
  const title = store.martialTitles.find((t) => t.id === app.titre_demande_id);
  const dossier = store.evidenceDossiers?.find((d) => d.sujet_id === app.id);
  const linkedDocs = store.authorityDocuments?.filter((d) => d.demande_titre_id === app.id) ?? [];

  const actions = canAdmin
    ? [
        { key: "documents_manquants", label: locale === "en" ? "Request documents" : "Demander documents" },
        { key: "recommande", label: locale === "en" ? "Recommend (demo)" : "Recommander (démo)" },
        { key: "approuve", label: locale === "en" ? "Approve (demo)" : "Approuver (démo)" },
        { key: "refuse", label: locale === "en" ? "Refuse (demo)" : "Refuser (démo)" },
        { key: "titre_attribue", label: locale === "en" ? "Grant title (demo)" : "Attribuer titre (démo)" },
      ]
    : [];

  return (
    <GsdReadableBlock testId={testId}>
      <p>
        <strong>{app.candidat_nom}</strong> — {title?.nom ?? app.titre_demande_id} <DemoBadge />
      </p>
      <p>
        {locale === "en" ? "Current grade" : "Grade actuel"} : {app.grade_actuel}
      </p>
      <p>
        {locale === "en" ? "Practice / teaching" : "Pratique / enseignement"} : {app.annees_pratique} /{" "}
        {app.annees_enseignement} {locale === "en" ? "years" : "ans"}
      </p>
      <p>
        {locale === "en" ? "Status" : "Statut"} :{" "}
        <StatusBadge
          label={titleLabel(APPLICATION_STATUSES, app.statut, locale)}
          tone="blue"
          testId={`app-status-${app.id}`}
        />
      </p>
      {app.documents?.length ? (
        <p>
          {locale === "en" ? "Documents" : "Documents"} : {app.documents.join(", ")}
        </p>
      ) : (
        <p className="muted">{locale === "en" ? "No documents attached." : "Aucun document joint."}</p>
      )}
      {app.notes_revision ? <p className="muted">{app.notes_revision}</p> : null}
      {dossier ? (
        <p data-testid={`app-dossier-score-${app.id}`}>
          {locale === "en" ? "Dossier completeness" : "Complétude dossier"} : <strong>{dossier.score_completude}%</strong>
          {dossier.documents_manquants?.length ? (
            <span className="muted"> — {locale === "en" ? "missing" : "manquants"} : {dossier.documents_manquants.length}</span>
          ) : null}
        </p>
      ) : null}
      {linkedDocs.length ? (
        <div data-testid={`app-linked-proofs-${app.id}`}>
          <strong>{locale === "en" ? "Linked proofs" : "Preuves associées"}</strong>
          <ul>
            {linkedDocs.map((d) => (
              <li key={d.id}>{d.titre}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <EvidenceSummaryBlock sujetId={app.id} testId={`evidence-app-${app.id}`} />
      {canAdmin ? (
        <div className="btn-row" data-testid="admin-app-actions">
          {actions.map((a) => (
            <button
              key={a.key}
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => onAdminAction(app.id, a.key)}
              data-testid={`admin-app-action-${a.key}-${app.id}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      ) : null}
    </GsdReadableBlock>
  );
}

export function TitleApplicationsPanel({
  mode = "admin",
  testId = "title-applications-panel",
}) {
  const locale = useLocale();
  const store = useDataStore();
  const { username } = useAuth();
  const ctx = getDemoContextForAccount(username);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newApp, setNewApp] = useState({
    titre_demande_id: "title-sensei",
    grade_actuel: "",
    annees_pratique: "",
    annees_enseignement: "",
    experience: "",
    contribution: "",
  });

  const rows = useMemo(() => {
    let list = store.titleApplications;
    if (mode === "sensei") {
      list = list.filter(
        (a) =>
          a.candidat_id === "demo-sensei-placeholder" ||
          a.dojo_id === (ctx?.dojoId || "demo-dojo-01"),
      );
    }
    return list.map((a) => {
      const dossier = store.evidenceDossiers?.find((d) => d.sujet_id === a.id);
      return {
        ...a,
        titreLabel: store.martialTitles.find((t) => t.id === a.titre_demande_id)?.nom ?? a.titre_demande_id,
        dossierScore: dossier?.score_completude ?? 0,
        missingDocs: dossier?.documents_manquants?.length ?? 0,
      };
    });
  }, [store.titleApplications, store.martialTitles, store.evidenceDossiers, mode, ctx]);

  const handleAdminAction = (id, status) => {
    updateTitleApplication(id, {
      statut: status,
      date_decision: status === "approuve" || status === "refuse" || status === "titre_attribue"
        ? new Date().toISOString().slice(0, 10)
        : null,
      decision: status,
      notes_revision:
        locale === "en"
          ? `[DEMO] Status updated to ${status}.`
          : `[DÉMO] Statut mis à jour : ${status}.`,
    });
    notifyDataUpdate();
  };

  const handleCreate = (e) => {
    e.preventDefault();
    const id = `app-demo-${Date.now()}`;
    addTitleApplication({
      id,
      candidat_id: "demo-sensei-placeholder",
      candidat_nom: locale === "en" ? "[DEMO] Demo Sensei" : "[DÉMO] Sensei démo",
      dojo_id: ctx?.dojoId || "demo-dojo-01",
      titre_demande_id: newApp.titre_demande_id,
      grade_actuel: newApp.grade_actuel || "2e dan [À VALIDER]",
      annees_pratique: Number(newApp.annees_pratique) || 0,
      annees_enseignement: Number(newApp.annees_enseignement) || 0,
      experience: newApp.experience,
      contribution: newApp.contribution,
      recommandation_par: null,
      documents: [],
      statut: "brouillon",
      decision: null,
      notes_revision: "",
      date_soumission: new Date().toISOString().slice(0, 10),
      date_decision: null,
      demo: true,
    });
    notifyDataUpdate();
    setShowCreate(false);
    setSelectedId(id);
  };

  const columns = [
    {
      key: "candidat_nom",
      label: locale === "en" ? "Candidate" : "Candidat",
      render: (r) => (
        <>
          {r.candidat_nom} {demoFlag(r)}
        </>
      ),
    },
    { key: "titreLabel", label: locale === "en" ? "Title requested" : "Titre demandé" },
    { key: "grade_actuel", label: locale === "en" ? "Grade" : "Grade" },
    {
      key: "annees_pratique",
      label: locale === "en" ? "Experience" : "Expérience",
      render: (r) => `${r.annees_pratique}a / ${r.annees_enseignement}e`,
    },
    {
      key: "dossierScore",
      label: locale === "en" ? "Dossier %" : "Dossier %",
      render: (r) => <span data-testid={`app-list-score-${r.id}`}>{r.dossierScore}%</span>,
    },
    {
      key: "missingDocs",
      label: locale === "en" ? "Missing" : "Manquants",
      render: (r) => r.missingDocs,
    },
    {
      key: "statut",
      label: locale === "en" ? "Status" : "Statut",
      render: (r) => (
        <StatusBadge
          label={titleLabel(APPLICATION_STATUSES, r.statut, locale)}
          tone={r.statut === "refuse" ? "neutral" : "blue"}
          testId={`app-list-status-${r.id}`}
        />
      ),
    },
  ];

  const selected = rows.find((r) => r.id === selectedId);

  return (
    <div data-testid={testId}>
      <InternalReferentialNotice testId="title-apps-referential-notice" />
      <DataTable
        rows={rows}
        columns={columns}
        searchFields={["candidat_nom", "statut"]}
        filters={[
          {
            field: "statut",
            label: locale === "en" ? "Status" : "Statut",
            options: Object.entries(APPLICATION_STATUSES).map(([v, l]) => ({
              value: v,
              label: locale === "en" ? l.en : l.fr,
            })),
          },
        ]}
        emptyMessage={locale === "en" ? "No applications." : "Aucune demande."}
        testId={`${testId}-table`}
      />

      {rows.length ? (
        <label style={{ display: "block", marginTop: "1rem" }}>
          {locale === "en" ? "View application" : "Voir demande"}
          <select
            value={selectedId || ""}
            onChange={(e) => setSelectedId(e.target.value || null)}
            data-testid={`${testId}-select`}
          >
            <option value="">—</option>
            {rows.map((r) => (
              <option key={r.id} value={r.id}>
                {r.candidat_nom} — {r.titreLabel}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {selected ? (
        <ApplicationDetail
          app={selected}
          locale={locale}
          canAdmin={mode === "admin"}
          onAdminAction={handleAdminAction}
          testId={`app-detail-${selected.id}`}
        />
      ) : null}

      {mode === "sensei" ? (
        <div className="title-app-create" data-testid="sensei-title-app-create">
          <p className="muted">
            {locale === "en"
              ? "You may request or recommend — you cannot self-grant a title."
              : "Vous pouvez demander ou recommander — vous ne pouvez pas vous auto-attribuer un titre."}
          </p>
          {!showCreate ? (
            <button type="button" className="btn-primary btn-sm" onClick={() => setShowCreate(true)} data-testid="sensei-create-app-btn">
              {locale === "en" ? "Create demo application" : "Créer demande démo"}
            </button>
          ) : (
            <form onSubmit={handleCreate} className="gsd-admin-panel__form">
              <label>
                {locale === "en" ? "Title requested" : "Titre demandé"}
                <select
                  value={newApp.titre_demande_id}
                  onChange={(e) => setNewApp({ ...newApp, titre_demande_id: e.target.value })}
                  data-testid="sensei-app-title-select"
                >
                  {store.martialTitles
                    .filter((t) => t.statut !== "futur")
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.nom}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                {locale === "en" ? "Current grade" : "Grade actuel"}
                <input
                  value={newApp.grade_actuel}
                  onChange={(e) => setNewApp({ ...newApp, grade_actuel: e.target.value })}
                />
              </label>
              <label>
                {locale === "en" ? "Practice years" : "Années pratique"}
                <input
                  type="number"
                  value={newApp.annees_pratique}
                  onChange={(e) => setNewApp({ ...newApp, annees_pratique: e.target.value })}
                />
              </label>
              <label>
                {locale === "en" ? "Teaching years" : "Années enseignement"}
                <input
                  type="number"
                  value={newApp.annees_enseignement}
                  onChange={(e) => setNewApp({ ...newApp, annees_enseignement: e.target.value })}
                />
              </label>
              <label>
                {locale === "en" ? "Expected documents" : "Documents attendus"}
                <p className="muted">
                  {store.martialTitles.find((t) => t.id === newApp.titre_demande_id)?.documents_requis}
                </p>
              </label>
              <div className="btn-row">
                <button type="submit" className="btn-primary btn-sm" data-testid="sensei-submit-app-btn">
                  {locale === "en" ? "Save draft" : "Enregistrer brouillon"}
                </button>
                <button type="button" className="btn-secondary btn-sm" onClick={() => setShowCreate(false)}>
                  {locale === "en" ? "Cancel" : "Annuler"}
                </button>
              </div>
            </form>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function TitleRegisterPanel({ testId = "title-register-panel" }) {
  const locale = useLocale();
  const store = useDataStore();

  const rows = store.titleRegister.map((r) => {
    const dossier = store.evidenceDossiers?.find((d) => d.sujet_id === r.id);
    return {
      ...r,
      validationStatut: dossier
        ? authDocLabelFromDossier(dossier, locale)
        : locale === "en"
          ? "Pending validation"
          : "Validation en attente",
    };
  });

  function authDocLabelFromDossier(d, loc) {
    const map = {
      incomplet: { fr: "Incomplet", en: "Incomplete" },
      en_revision: { fr: "En révision", en: "Under review" },
      complet_a_valider: { fr: "À valider", en: "To validate" },
    };
    const e = map[d.statut_dossier];
    return e ? (loc === "en" ? e.en : e.fr) : d.statut_dossier;
  }

  const columns = [
    {
      key: "numero_registre",
      label: locale === "en" ? "Register #" : "N° registre",
      render: (r) => (
        <>
          {r.numero_registre} {demoFlag(r)}
        </>
      ),
    },
    { key: "nom", label: locale === "en" ? "Person" : "Personne" },
    { key: "titre_nom", label: locale === "en" ? "Title" : "Titre" },
    { key: "date_attribution", label: locale === "en" ? "Date" : "Date" },
    {
      key: "attribue_par",
      label: locale === "en" ? "Granted by" : "Attribué par",
      render: (r) => {
        const auth = store.titleAuthority.find((a) => a.id === r.attribue_par);
        return auth?.nom ?? r.attribue_par;
      },
    },
    {
      key: "statut",
      label: locale === "en" ? "Status" : "Statut",
      render: (r) => titleLabel(REGISTER_STATUSES, r.statut, locale),
    },
    {
      key: "certificat_statut",
      label: locale === "en" ? "Certificate" : "Certificat",
      render: (r) => (
        <span data-testid={`cert-status-${r.id}`}>
          {titleLabel(CERTIFICAT_STATUSES, r.certificat_statut, locale)}
        </span>
      ),
    },
    {
      key: "validationStatut",
      label: locale === "en" ? "Validation" : "Validation",
      render: (r) => <span data-testid={`reg-validation-${r.id}`}>{r.validationStatut}</span>,
    },
  ];

  return (
    <div data-testid={testId}>
      <InternalReferentialNotice />
      <p className="muted" data-testid="register-pdf-future-notice">
        {locale === "en"
          ? "Official PDF certificates — Phase 2+. No official certificate generated in Phase 1."
          : "Certificats PDF officiels — Phase 2+. Aucun certificat officiel généré en Phase 1."}
      </p>
      <DataTable
        rows={rows}
        columns={columns}
        searchFields={["nom", "titre_nom", "numero_registre"]}
        emptyMessage={locale === "en" ? "Register empty — demo entries only." : "Registre vide — entrées démo seulement."}
        testId={`${testId}-table`}
      />
    </div>
  );
}

export function TitleAuthorityPanel({ testId = "title-authority-panel" }) {
  const locale = useLocale();
  const store = useDataStore();
  const auth = store.titleAuthority[0];

  if (!auth) {
    return (
      <p className="muted" data-testid={`${testId}-empty`}>
        {locale === "en" ? "No authority record." : "Aucune fiche autorité."}
      </p>
    );
  }

  return (
    <div data-testid={testId}>
      <GsdReadableBlock testId="authority-warning-block">
        <p className="title-authority-warning" data-testid="authority-no-external-claim">
          {locale === "en"
            ? "No external recognition must be displayed as official until authority documents are provided and validated."
            : "Aucune reconnaissance externe ne doit être affichée comme officielle tant que les documents d'autorité ne sont pas fournis et validés."}
        </p>
      </GsdReadableBlock>

      <GsdReadableBlock testId="authority-detail-block">
        <h3>
          {auth.nom} <DemoBadge />
        </h3>
        <p data-testid="authority-grade">
          {locale === "en" ? "Grade" : "Grade"} : <strong>{auth.grade}</strong>
        </p>
        <p>
          {locale === "en" ? "Title" : "Titre"} : {auth.titre}
        </p>
        <p data-testid="authority-japan-stay">
          {locale === "en" ? "Japan stay" : "Séjour Japon"} :{" "}
          {auth.sejour_japon
            ? locale === "en"
              ? "Yes [TO VALIDATE]"
              : "Oui [À VALIDER]"
            : locale === "en"
              ? "No"
              : "Non"}
        </p>
        <p data-testid="authority-source">
          {locale === "en" ? "Authority source" : "Source autorité"} : {auth.source_autorite}
        </p>
        <p>
          {locale === "en" ? "Validation status" : "Statut validation"} :{" "}
          <StatusBadge
            label={titleLabel(AUTHORITY_STATUSES, auth.statut_validation, locale)}
            tone="yellow"
            testId="authority-validation-status"
          />
        </p>
        <p>
          {locale === "en" ? "Scope / limits" : "Portée / limites"} : {auth.limites}
        </p>
        <p className="muted">{auth.notes}</p>
        <p className="muted">
          {locale === "en" ? "Proof documents" : "Documents preuve"} :{" "}
          {auth.documents_preuve?.length
            ? auth.documents_preuve.join(", ")
            : locale === "en"
              ? "[DOCUMENTS TO BE PROVIDED]"
              : "[DOCUMENTS À FOURNIR]"}
        </p>
      </GsdReadableBlock>

      <EvidenceSummaryBlock sujetId="auth-demo-responsable" testId="authority-evidence-summary" />

      <InternalReferentialNotice testId="authority-referential-notice" />
    </div>
  );
}

export function SempaiTitlePathPanel({ testId = "sempai-title-path" }) {
  const locale = useLocale();
  return (
    <GsdReadableBlock testId={testId}>
      <h3>{locale === "en" ? "Title path (consultation)" : "Parcours des titres (consultation)"}</h3>
      <p data-testid="sempai-role-vs-title">
        {locale === "en"
          ? "Your Sempai app role assists classes — it is not the same as a recognized martial title (Sensei, Shihan, etc.)."
          : "Votre rôle Sempai dans l'application assiste les cours — il ne se confond pas avec un titre martial reconnu (Sensei, Shihan, etc.)."}
      </p>
      <p className="muted" data-testid="sempai-no-validate">
        {locale === "en"
          ? "You cannot validate a title application."
          : "Vous ne pouvez pas valider une demande de titre."}
      </p>
      <MartialTitlesReferentialPanel testId="sempai-titles-ref" />
    </GsdReadableBlock>
  );
}

export function StudentTitlesView({ testId = "student-titles-view" }) {
  const locale = useLocale();
  const { username } = useAuth();
  const ctx = getDemoContextForAccount(username);
  const store = useDataStore();
  const studentId = ctx?.studentId;
  const registerEntries = studentId ? store.titleRegister.filter((r) => r.personne_id === studentId) : [];
  const progression = studentId ? store.progressions.find((p) => p.student_id === studentId) : null;
  const belt = progression
    ? store.ceintures.find((b) => b.id === progression.ceinture_actuelle_id)
    : null;

  return (
    <GsdReadableBlock testId={testId}>
      <InternalReferentialNotice />
      <TerminologyPanel testId="student-title-terminology" />

      <h3>{locale === "en" ? "My situation" : "Ma situation"}</h3>
      <p data-testid="student-current-grade">
        {locale === "en" ? "Current grade (belt)" : "Grade actuel (ceinture)"} :{" "}
        <strong>{belt?.nom ?? (locale === "en" ? "White belt [DEMO]" : "Ceinture blanche [DÉMO]")}</strong>
      </p>
      <p data-testid="student-current-title">
        {locale === "en" ? "Recognized title" : "Titre reconnu"} :{" "}
        {registerEntries.length
          ? registerEntries.map((r) => r.titre_nom).join(", ")
          : locale === "en"
            ? "None yet — advanced titles require experience and teaching."
            : "Aucun pour l'instant — les titres avancés exigent expérience et enseignement."}
      </p>
      <p data-testid="student-app-role">
        {locale === "en" ? "App role" : "Rôle applicatif"} :{" "}
        <strong>{locale === "en" ? "Student" : "Étudiant"}</strong>
      </p>

      <p className="muted">
        {locale === "en"
          ? "Advanced titles (Shihan, Renshi, Kyoshi, Hanshi) concern long-term experience and teaching — not app permissions."
          : "Les titres avancés (Shihan, Renshi, Kyoshi, Hanshi) concernent l'expérience et l'enseignement à long terme — pas les permissions applicatives."}
      </p>

      <div className="btn-row" data-testid="student-title-actions">
        <button type="button" className="btn-secondary btn-sm" disabled data-testid="student-future-request-btn">
          {locale === "en" ? "Future request (disabled)" : "Demande future (désactivée)"}
        </button>
        <Link to="/mon-sensei" className="btn-primary btn-sm" data-testid="student-talk-sensei-link">
          {locale === "en" ? "Talk to my Sensei" : "Parler à mon Sensei"}
        </Link>
      </div>
      <StudentProofsExplanation />
    </GsdReadableBlock>
  );
}
